import React, { useState } from 'react';
import { X, Loader2, CheckCircle, Building2 } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LeadCapturePopup = ({ 
  isOpen, 
  onClose, 
  redirectUrl, 
  title = "Access Our Exclusive Inventory",
  subtitle = "Please provide your details to access our land inventory database."
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name.trim() || !formData.email.trim()) {
      setError('Please fill in all required fields.');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/inventory-leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          source: 'land_inventory',
          redirect_url: redirectUrl
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setIsSuccess(true);
        // Redirect after a brief success message
        setTimeout(() => {
          if (redirectUrl) {
            window.open(redirectUrl, '_blank', 'noopener,noreferrer');
          }
          onClose();
          // Reset state for next time
          setIsSuccess(false);
          setFormData({ name: '', email: '', phone: '', company: '' });
        }, 1500);
      } else {
        setError(data.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting lead:', err);
      setError('Unable to submit. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
      data-testid="lead-capture-popup-backdrop"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      
      {/* Modal */}
      <div 
        className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden transform transition-all"
        style={{
          animation: 'popupSlideIn 0.3s ease-out'
        }}
        data-testid="lead-capture-popup-modal"
      >
        {/* Header */}
        <div className="bg-[#0A1A50] px-6 py-8 text-center relative">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors disabled:opacity-50"
            data-testid="lead-capture-close-btn"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          
          <h2 className="text-2xl font-light text-white mb-2">
            {title}
          </h2>
          <p className="text-white/80 text-sm font-light">
            {subtitle}
          </p>
        </div>

        {/* Form */}
        <div className="p-6">
          {isSuccess ? (
            <div className="text-center py-8" data-testid="lead-capture-success">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-[#2d2d2d] mb-2">Thank You!</h3>
              <p className="text-[#B5BBC4]">Redirecting you to the inventory...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm" data-testid="lead-capture-error">
                  {error}
                </div>
              )}
              
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-[#2d2d2d] mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0A1A50]/20 focus:border-[#0A1A50] transition-all"
                  disabled={isSubmitting}
                  data-testid="lead-capture-name-input"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-[#2d2d2d] mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0A1A50]/20 focus:border-[#0A1A50] transition-all"
                  disabled={isSubmitting}
                  data-testid="lead-capture-email-input"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-[#2d2d2d] mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Enter your phone number"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0A1A50]/20 focus:border-[#0A1A50] transition-all"
                  disabled={isSubmitting}
                  data-testid="lead-capture-phone-input"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-[#2d2d2d] mb-1">
                  Company Name
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Enter your company name"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0A1A50]/20 focus:border-[#0A1A50] transition-all"
                  disabled={isSubmitting}
                  data-testid="lead-capture-company-input"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#0A1A50] hover:bg-[#0A1A50]/90 text-white py-6 rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2"
                data-testid="lead-capture-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Get Access'
                )}
              </Button>

              <p className="text-xs text-center text-[#B5BBC4] mt-4">
                By submitting, you agree to our privacy policy. We will never share your information.
              </p>
            </form>
          )}
        </div>
      </div>

      <style>{`
        @keyframes popupSlideIn {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default LeadCapturePopup;
